import React, { useCallback, useState } from "react";
import cx from "classnames";
import {
  getIsAncestorDragging,
  getScrollToIndex,
} from "components/ProjectTree/utils";
import { toast } from "react-toastify";
import {
  useAddedProductStore,
  useCopiedItemStore,
  useProjectDataStore,
  useProjectUIStore,
} from "stores/projectStore";
import * as identify from "utils/treeUtils";
import { Folder } from "./Folder/Folder";
import { useIndentComponent } from "./useIndentComponent";
import { NodeLayout } from "./NodeLayout";
import { Configuration } from "./Configuration/Configuration";
import { NodeVisibility } from "./NodeVisibility";

export const Node = ({
  node,
  tree,
  style,
  dragHandle,
  options,
  nodeActions,
  translations,
  setRedrawIndex,
  setScrollToIndex,
  navigateToProduct,
  lang,
  navigateToCustomProduct,
}) => {
  const copiedItem = useCopiedItemStore((state) => state.copiedItem);
  const setCopiedItem = useCopiedItemStore((state) => state.setCopiedItem);
  const projectName = useProjectDataStore((state) => state.projectName);
  const toggleNode = useProjectUIStore((state) => state.toggleNode);
  const setHasAddedNode = useAddedProductStore(
    (state) => state.setHasAddedNode,
  );
  const setAddedProducts = useAddedProductStore(
    (state) => state.setAddedProducts,
  );
  // Props
  const { data, rowIndex, children, isOpen, isDragging, willReceiveDrop } =
    node;

  // Hooks
  const [isEditMode, setIsEditMode] = useState(false);
  const indent = useIndentComponent(node, options);

  const isAncestorDragging = getIsAncestorDragging(node);

  const isPasteAllowed =
    copiedItem &&
    !(identify.isAnyProduct(copiedItem.data) && identify.isRoot(data)) &&
    (identify.isFolder(data) || identify.isRoot(data));

  const handlePasteNode = useCallback(() => {
    if (!isPasteAllowed) {
      return;
    }
    setRedrawIndex(rowIndex);
    nodeActions.pasteNode(node, (res) => {
      if (options.productAddingModal) {
        if (res?.error) {
          toast.error(res.error);
        } else {
          setAddedProducts(res.subTree.id);

          const {
            subTree: { productName },
          } = res;
          setHasAddedNode();
          toast.success(
            translations.product.productAddedSuccess({
              productName,
              projectName,
            }),
          );
        }
      }
    });
    if (options.resetOnPaste) {
      setCopiedItem(null);
    }
    if (!isOpen) {
      node.toggle();
      toggleNode(data.id);
    }
    const index = getScrollToIndex(
      node,
      identify.isAnyProduct(copiedItem.data),
    );
    setScrollToIndex(index);
  }, [
    isPasteAllowed,
    setRedrawIndex,
    rowIndex,
    nodeActions,
    node,
    options.resetOnPaste,
    options.productAddingModal,
    isOpen,
    copiedItem,
    setScrollToIndex,
    setAddedProducts,
    setHasAddedNode,
    translations.product,
    projectName,
    setCopiedItem,
    toggleNode,
    data,
  ]);

  const dragNode =
    tree.dragNodes && tree.dragNodes.length > 0 ? tree.dragNodes[0] : null;

  // Handlers
  const handleToggleEditMode = useCallback(
    (type = false) => {
      if (
        identify.isFolder(data) ||
        identify.isProduct(data) ||
        identify.isCustomProduct(data) ||
        identify.isProductAccessory(data)
      ) {
        if (!isEditMode) {
          node.edit();
          setIsEditMode(type);
          return;
        }
        node.reset();
        setIsEditMode(false);
      }
    },
    [data, isEditMode, node],
  );

  const handleToggleNodeActiveStatus = useCallback(
    () => nodeActions.toggleNodeActiveStatus(node),
    [nodeActions, node],
  );

  const onDoubleClick = useCallback(() => {
    if (isEditMode || copiedItem) {
      return;
    }
    if (identify.isProduct(data) || identify.isProductAccessory(data)) {
      navigateToProduct(data);
    }
    if (
      (identify.isTroxNetcom(data) && identify.isAnyProduct(data)) ||
      identify.isCustomProduct(data)
    ) {
      navigateToCustomProduct(data);
    }
  }, [
    copiedItem,
    data,
    isEditMode,
    navigateToCustomProduct,
    navigateToProduct,
  ]);

  const productIdURL =
    new URL(window.location.href).searchParams.get("productId") || null;

  const isActiveProductNode = node.data?.id === productIdURL;

  return (
    <div
      ref={!isEditMode ? dragHandle : null}
      style={style}
      className={cx("w-100 h-100", {
        "pe-9": options.isFullpage,
        "pe-2": !options.isFullpage,
      })}
    >
      <div className="w-100 h-100 d-flex flex-row">
        {indent}
        <NodeLayout
          node={node}
          options={options}
          hasChildren={children && children.length > 0}
          isOpen={isOpen}
          isAncestorDragging={isAncestorDragging}
          isDragging={isDragging}
          willReceiveDrop={willReceiveDrop}
          onDoubleClick={onDoubleClick}
          dragNode={dragNode}
          onClick={handlePasteNode}
          isPasteAllowed={isPasteAllowed}
          productIdURL={productIdURL}
          isActiveProductNode={isActiveProductNode}
        >
          {identify.isAnyProduct(node.data) ? (
            <Configuration
              node={node}
              pimId={node.data.pimId}
              productName={node.data.productName}
              presentation={node.data.presentation}
              options={options}
              translations={translations}
              navigateToProduct={navigateToProduct}
              nodeActions={nodeActions}
              data={node.data}
              handleToggleEditMode={handleToggleEditMode}
              setIsEditMode={setIsEditMode}
              isEditMode={isEditMode}
              lang={lang}
              navigateToCustomProduct={navigateToCustomProduct}
            />
          ) : (
            <Folder
              node={node}
              tree={tree}
              style={style}
              options={options}
              nodeActions={nodeActions}
              translations={translations}
              setRedrawIndex={setRedrawIndex}
              willReceiveDrop={willReceiveDrop}
              handleToggleEditMode={handleToggleEditMode}
              setIsEditMode={setIsEditMode}
              isEditMode={isEditMode}
              setScrollToIndex={setScrollToIndex}
              data={data}
              handleToggleNodeActiveStatus={handleToggleNodeActiveStatus}
            />
          )}
        </NodeLayout>
        <div className="ps-2 h-100 d-flex align-items-center">
          <NodeVisibility node={node} onClick={handleToggleNodeActiveStatus} />
        </div>
      </div>
    </div>
  );
};
